import React, { useState, useEffect } from 'react';
import './App.css';
import { useAuth } from './hooks/useAuth';
import NavigationDrawer from './components/NavigationDrawer';
import ChatInterface from './components/ChatInterface';
import EnhancedAnalytics from './components/analytics/EnhancedAnalytics';
import AdminDashboard from './components/admin/AdminDashboard';

// Error boundary component
class ErrorBoundary extends React.Component {
  constructor(props) {
    super(props);
    this.state = { hasError: false, error: null };
  }

  static getDerivedStateFromError(error) {
    return { hasError: true, error };
  }

  componentDidCatch(error, errorInfo) {
    console.error('Error caught by boundary:', error, errorInfo);
  }

  render() {
    if (this.state.hasError) {
      return (
        <div className="error-boundary">
          <h3>Component Error</h3>
          <p>The login form encountered an error:</p>
          <p className="error-details">{this.state.error?.message || 'Unknown error'}</p>
          <button 
            onClick={() => this.setState({ hasError: false, error: null })}
            className="retry-button"
          >
            Try Again
          </button>
        </div>
      );
    }

    return this.props.children;
  }
}

function App() {
  const [loginFormError, setLoginFormError] = useState(null);
  const [LoginFormComponent, setLoginFormComponent] = useState(null);
  const [isDrawerOpen, setIsDrawerOpen] = useState(false);
  const [currentView, setCurrentView] = useState('chat');
  
  // Always call useAuth hook
  const { user, loading, error: authError, logout } = useAuth();

  useEffect(() => {
    const loadLoginForm = async () => {
      try {
        // Load the LoginForm component
        const LoginFormModule = await import('./components/LoginForm');
        const LoginForm = LoginFormModule.default;
        
        if (LoginForm) {
          setLoginFormComponent(() => LoginForm);
        } else {
          setLoginFormError('LoginForm is not a valid component');
        }
      } catch (error) {
        console.error('LoginForm import error:', error);
        setLoginFormError(`Import failed: ${error.message}`);
      }
    };

    loadLoginForm();
  }, []);

  // Show loading state
  if (loading) {
    return (
      <div className="App">
        <div className="auth-container">
          <h1>Brookmar</h1>
          <div className="loading-container">
            <p>Loading authentication...</p>
          </div>
        </div>
      </div>
    );
  }

  // Show auth error
  if (authError) {
    return (
      <div className="App">
        <div className="auth-container">
          <h1>Brookmar</h1>
          <div className="error-container">
            <p><strong>Authentication Error:</strong> {authError}</p>
          </div>
        </div>
      </div>
    );
  }

  // Show authenticated user state
  if (user) {
    return (
      <div className="App">
        {/* Navigation Drawer */}
        <NavigationDrawer
          isOpen={isDrawerOpen}
          onToggle={() => setIsDrawerOpen(!isDrawerOpen)}
          user={user}
          onLogout={logout}
          currentView={currentView}
          onViewChange={setCurrentView}
        />
        
        {/* Main Content */}
        <div className="main-content">
          {/* Top Bar */}
          <div className="top-bar">
            <button 
              className="menu-button"
              onClick={() => setIsDrawerOpen(!isDrawerOpen)}
            >
              ☰
            </button>
            <div className="top-bar-title">
              <h1>Brookmar Email Intelligence</h1>
            </div>
            <div className="top-bar-user">
              <span>{user.displayName || user.email}</span>
            </div>
          </div>
          
          {/* Content based on current view */}
          {currentView === 'chat' ? (
            <ChatInterface onLogout={logout} />
                  ) : currentView === 'analytics' ? (
          <EnhancedAnalytics />
          ) : currentView === 'admin' ? (
            <AdminDashboard />
          ) : (
            <ChatInterface onLogout={logout} />
          )}
        </div>
      </div>
    );
  }

  // Show login form
  return (
    <div className="App">
      <div className="auth-container">
        <h1>Brookmar</h1>
        <p className="auth-subtitle">Sign in to your account</p>
        
        {LoginFormComponent ? (
          <ErrorBoundary>
            <LoginFormComponent />
          </ErrorBoundary>
        ) : loginFormError ? (
          <div className="error-container">
            <p><strong>Error:</strong> {loginFormError}</p>
          </div>
        ) : (
          <div className="loading-container">
            <p>Loading authentication form...</p>
          </div>
        )}
      </div>
    </div>
  );
}

export default App;
